
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Newsletter subscription:', email);
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-lg' : 'bg-white/10 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-white">RIGLESA</h1>
          </div>
          <div className="hidden md:flex items-center space-x-12">
            <a href="#about" className="text-white/70 hover:text-white text-sm uppercase tracking-wider transition-colors cursor-pointer">About</a>
            <a href="#security" className="text-white/70 hover:text-white text-sm uppercase tracking-wider transition-colors cursor-pointer">Security</a>
            <a href="#contact" className="text-white/70 hover:text-white text-sm uppercase tracking-wider transition-colors cursor-pointer">Contact</a>
          </div>
          <button className="px-6 py-2 border-2 border-cyan-400 text-white rounded-full hover:bg-cyan-400/20 transition-all whitespace-nowrap cursor-pointer">
            Get Protected
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900">
        {/* Animated Network Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.3) 0%, transparent 50%),
                             radial-gradient(circle at 80% 80%, rgba(6, 182, 212, 0.3) 0%, transparent 50%),
                             radial-gradient(circle at 40% 20%, rgba(6, 182, 212, 0.2) 0%, transparent 50%)`
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-8 py-32 relative z-10 w-full">
          <div className="ml-[5%]">
            <h2 className="text-7xl font-extrabold text-white leading-tight mb-8" style={{ textShadow: '0 0 30px rgba(6, 182, 212, 0.3)' }}>
              SECURE YOUR<br />
              DIGITAL LIFE<br />
              WITH CONFIDENCE
            </h2>
            <p className="text-2xl text-white/70 mb-12 max-w-2xl font-light">
              Enterprise-grade VPN protection for your privacy and security.<br />
              Connect safely from anywhere in the world.
            </p>
            <button className="group relative px-12 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-2xl text-lg font-semibold hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 flex items-center space-x-4 whitespace-nowrap cursor-pointer">
              <span>Download Now</span>
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-shield-check-line text-white"></i>
              </div>
            </button>
          </div>
        </div>

        {/* Security Badge */}
        <div className="absolute bottom-12 right-12 bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
          <div className="flex items-center space-x-3">
            <i className="ri-lock-line text-cyan-400 text-3xl"></i>
            <div>
              <p className="text-white font-bold text-lg whitespace-nowrap">256-bit Encryption</p>
              <p className="text-white/60 text-sm whitespace-nowrap">Military Grade</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-3">
              <p className="text-cyan-500 text-xs uppercase tracking-widest mb-4 font-semibold">WHO WE ARE</p>
              <h3 className="text-5xl font-serif font-bold text-gray-900 mb-8 leading-tight">
                Protecting Privacy<br />
                Through Innovation
              </h3>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed max-w-xl">
                <p>
                  RIGLESA S.R.L. is a leading cybersecurity company dedicated to protecting your digital privacy. Based in Santa Rosa, Argentina, we develop cutting-edge VPN solutions that ensure your online activities remain private and secure.
                </p>
                <p>
                  Our mission is to provide enterprise-grade security technology that's accessible to everyone. We believe that privacy is a fundamental right, and we're committed to delivering the most reliable and user-friendly VPN service available.
                </p>
                <p>
                  With advanced encryption protocols and a strict no-logs policy, RIGLESA ensures your data stays yours. Our global infrastructure and commitment to innovation make us the trusted choice for secure internet access.
                </p>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="relative transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://public.readdy.ai/ai/img_res/e2fd1ebd6663da59ac96d02ef60bb728.jpg"
                  alt="Security Technology Visualization"
                  className="w-full h-auto rounded-3xl shadow-2xl object-cover"
                />
              </div>
            </div>
          </div>
          
          {/* Company Info Strip */}
          <div className="mt-16 bg-slate-900 rounded-2xl p-8">
            <div className="flex flex-wrap justify-between items-center gap-8 text-white">
              <div className="flex items-center space-x-3">
                <i className="ri-building-line text-cyan-400 text-2xl"></i>
                <span className="font-bold text-lg">RIGLESA S.R.L.</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-map-pin-line text-cyan-400 text-2xl"></i>
                <span className="text-sm">Santa Rosa, Argentina</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-shield-check-line text-cyan-400 text-2xl"></i>
                <span className="text-sm font-light">Trusted Security Solutions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="security" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-gray-500 font-serif text-lg mb-4">Our Technology</p>
            <h3 className="text-6xl font-bold text-gray-900 leading-tight">
              MILITARY-GRADE<br />
              PROTECTION
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1 - Encryption */}
            <div className="group bg-purple-700 rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-96 flex flex-col">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-lock-line text-white text-3xl"></i>
              </div>
              <h4 className="text-3xl font-bold text-white mb-4">AES-256 Encryption</h4>
              <p className="text-white/70 text-sm leading-relaxed flex-grow">
                Bank-level encryption that protects your data with the same security standards used by governments and financial institutions worldwide.
              </p>
              <div className="h-1 w-16 bg-cyan-400 mt-6"></div>
            </div>

            {/* Card 2 - No-Logs */}
            <div className="group bg-gradient-to-br from-teal-500 to-cyan-500 rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-96 flex flex-col">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-white text-3xl"></i>
              </div>
              <h4 className="text-3xl font-bold text-white mb-4">No-Logs Policy</h4>
              <p className="text-white/70 text-sm leading-relaxed flex-grow">
                We never track, collect, or store your online activities. Your privacy is guaranteed with our strict zero-logs commitment.
              </p>
              <div className="h-1 w-16 bg-white mt-6"></div>
            </div>

            {/* Card 3 - Global Servers */}
            <div className="group bg-gray-900 rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-96 flex flex-col">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-global-line text-white text-3xl"></i>
              </div>
              <h4 className="text-3xl font-bold text-white mb-4">Global Servers</h4>
              <p className="text-white/70 text-sm leading-relaxed flex-grow">
                Access our worldwide network of high-speed servers strategically located across multiple continents for optimal performance.
              </p>
              <div className="h-1 w-16 bg-cyan-400 mt-6"></div>
            </div>

            {/* Card 4 - Kill Switch */}
            <div className="group bg-red-500 rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer h-96 flex flex-col">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shut-down-line text-white text-3xl"></i>
              </div>
              <h4 className="text-3xl font-bold text-white mb-4">Kill Switch</h4>
              <p className="text-white/70 text-sm leading-relaxed flex-grow">
                Automatic protection that instantly blocks all internet traffic if your VPN connection drops, ensuring your data never leaks.
              </p>
              <div className="h-1 w-16 bg-white mt-6"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-cyan-600 to-slate-900" style={{
          clipPath: 'polygon(0 0, 85% 0, 70% 100%, 0 100%)'
        }}></div>
        <div className="absolute inset-0 bg-slate-900" style={{
          clipPath: 'polygon(85% 0, 100% 0, 100% 100%, 70% 100%)'
        }}></div>

        <div className="max-w-7xl mx-auto px-8 relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-12">
            <div className="lg:w-3/5">
              <h3 className="text-6xl font-bold text-white leading-tight mb-6">
                START PROTECTING<br />
                YOUR DATA TODAY
              </h3>
              <p className="text-white/80 text-xl mb-8 font-light">
                Join thousands securing their digital presence
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="px-12 py-4 bg-white text-cyan-600 rounded-full font-bold text-lg hover:shadow-2xl transition-all flex items-center space-x-3 whitespace-nowrap cursor-pointer">
                  <span>Get Started</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button className="px-12 py-4 border-2 border-white text-white rounded-full font-bold text-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                  Learn More
                </button>
              </div>
            </div>

            <div className="lg:w-2/5 flex justify-end">
              <div className="bg-slate-900/80 backdrop-blur-md rounded-2xl p-8 border border-white/10 max-w-md">
                <h4 className="text-white text-xl font-bold mb-6">Contact Us</h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <i className="ri-mail-line text-cyan-400 text-xl mt-1"></i>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Email</p>
                      <a href="mailto:information@riglesasrl.com" className="text-white text-sm hover:text-cyan-400 transition-colors">information@riglesasrl.com</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="ri-phone-line text-cyan-400 text-xl mt-1"></i>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Phone</p>
                      <a href="tel:+12513224602" className="text-white text-sm hover:text-cyan-400 transition-colors">+1 2513224602</a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <i className="ri-map-pin-line text-cyan-400 text-xl mt-1"></i>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Address</p>
                      <p className="text-white text-sm leading-relaxed">
                        1 de Mayo 426<br />
                        Santa Rosa, L6302ECJ<br />
                        Argentina
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
            {/* Brand Column */}
            <div className="md:col-span-4">
              <h5 className="text-2xl font-bold mb-3">RIGLESA</h5>
              <p className="text-gray-400 text-sm mb-4">Secure VPN Solutions</p>
              <p className="text-gray-500 text-xs leading-relaxed">
                Leading cybersecurity company providing enterprise-grade VPN protection for individuals and businesses worldwide. Your privacy is our priority.
              </p>
            </div>

            {/* Quick Links */}
            <div className="md:col-span-3">
              <h6 className="text-white font-bold mb-4">Company</h6>
              <ul className="space-y-3">
                <li><a href="#about" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#security" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Technology</a></li>
                <li><a href="#security" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Security</a></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div className="md:col-span-3">
              <h6 className="text-white font-bold mb-4">Legal</h6>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Cookie Policy</a></li>
                <li><a href="#" className="text-gray-400 text-sm hover:text-cyan-400 hover:underline transition-colors cursor-pointer">Compliance</a></li>
              </ul>
            </div>

            {/* Copyright */}
            <div className="md:col-span-2 text-right">
              <p className="text-white text-sm font-serif mb-2">© 2025 RIGLESA S.R.L.</p>
              <p className="text-gray-500 text-xs mb-4">All rights reserved</p>
              <div className="flex justify-end space-x-4">
                <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer">
                  <i className="ri-github-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Visual Section */}
          <div className="grid grid-cols-1 md:grid-cols-10 gap-8 pt-12 border-t border-gray-800">
            {/* Decorative Image */}
            <div className="md:col-span-3">
              <div className="w-full h-48 rounded-2xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Abstract%20glowing%20network%20nodes%20visualization%20on%20dark%20background%20with%20cyan%20luminous%20connections%20representing%20secure%20data%20encryption%20and%20cybersecurity%20technology%2C%20digital%20security%20concept%20with%20geometric%20patterns%20and%20light%20effects%2C%20modern%20tech%20aesthetic%2C%20professional%20corporate%20style&width=400&height=300&seq=footer-visual&orientation=landscape"
                  alt="Security Network"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Trust Statement */}
            <div className="md:col-span-4 flex items-center justify-center">
              <p className="text-white font-serif italic text-2xl leading-relaxed text-center">
                Your Privacy,<br />
                Our Priority,<br />
                Always Protected
              </p>
            </div>

            {/* Newsletter */}
            <div className="md:col-span-3">
              <h6 className="text-white text-lg font-bold mb-2">Stay Secure</h6>
              <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                Get security updates and tips<br />
                delivered to your inbox
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                  required
                />
                <button 
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center space-x-2 whitespace-nowrap cursor-pointer"
                >
                  <span>Subscribe</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>
          </div>

          {/* Readdy Link */}
          <div className="mt-12 pt-8 border-t border-gray-800 text-center">
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 text-xs hover:text-cyan-400 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
